import { createDocEntryStore } from './docStore';
import { runEstimate } from './dimensionsRun';
import { RUN_MODEL } from './models';
import { haltGate } from './runHalt';

// THE ESTIMATE PRESS, over several parts: which one is out, which are still owed, which failed.
//
// In a store and not in the table, because the press is beside the phase button now (see
// EstimateInfo) while the cells it fills are in the table — and the cells are where each part
// says it is being answered.
type Snapshot = {
  // The part whose call is out, or null.
  current: number | null;
  // THE PARTS THIS PRESS STILL OWES, so all of them wait rather than only the one in flight:
  // a cell next in the queue is a cell whose size is about to be replaced. Emptied as each
  // lands.
  queued: number[];
  // Finished out of asked, for the badge's spinner. Null when nothing is running.
  progress: { done: number; total: number } | null;
  // The parts whose last estimate failed.
  failed: number[];
};

const store = createDocEntryStore<Snapshot>(() => ({
  snapshot: { current: null, queued: [], progress: null, failed: [] },
}));

export const useEstimateRun = store.useSnapshot;

// ONE AFTER THE OTHER. In sequence rather than at once: each is a vision call over the same
// few photographs, and a dozen fired together is how a rate limit is found.
export async function runEstimates(
  docId: string, parts: number[], productName: string, categories: string[],
): Promise<void> {
  if (!parts.length || !productName || store.get(docId).progress) return;
  const failedBefore = store.get(docId).failed.filter((i) => !parts.includes(i));
  store.patch(docId, {
    queued: [...parts], progress: { done: 0, total: parts.length }, failed: failedBefore,
  });
  const failed = [...failedBefore];
  // STOP SENDS NO NEXT PART (see runHalt): the one out lands and is kept.
  const halted = haltGate(docId);
  try {
    for (const [n, at] of parts.entries()) {
      if (halted()) break;
      store.patch(docId, { current: at });
      try {
        await runEstimate(docId, at, productName, RUN_MODEL, categories);
      } catch {
        failed.push(at);
      }
      store.patch(docId, {
        queued: parts.slice(n + 1), progress: { done: n + 1, total: parts.length }, failed: [...failed],
      });
    }
  } finally {
    store.patch(docId, { current: null, queued: [], progress: null });
  }
}
